/** Account sessions and the legacy instance invite gate. */

import * as api from './api';
import { loadProviderFeatures } from './provider-policy.svelte';
import { captureRoomInvitation } from '../social/invite-handoff';

// Capture before the gate renders or any asynchronous session check starts.
captureRoomInvitation();

/*
 * `offline` used to mean "cannot reach the server, show the wall". It still
 * does for a browser that has NEVER been let in. For one that has, it means
 * something else now: the shell opened from cache (web/public/sw.js), the
 * downloads are on the disk, and the only thing the network was going to add
 * was confirmation of who you are - which the last successful session already
 * said. So `offline` + a remembered session opens the app in OFFLINE MODE:
 * Downloads and what is on this device, nothing that needs a server.
 *
 * The owner found this the direct way, on a plane: the app loaded and then
 * showed "We couldn't connect to Jovii" over the films he had downloaded for
 * exactly that flight.
 */
export type GateState = 'checking' | 'open' | 'locked' | 'offline';

const REMEMBERED = 'jt.session.v1';

/** What we last knew for certain about this browser's standing. */
interface Remembered {
  accounts: boolean;
  account: api.SessionState['account'] | null;
  at: number;
}

function remember(state: api.SessionState): void {
  try {
    const value: Remembered = {
      accounts: state.accounts === true,
      account: state.account ?? null,
      at: Date.now()
    };
    localStorage.setItem(REMEMBERED, JSON.stringify(value));
  } catch {
    /* private mode: offline mode is simply unavailable, which is the old behaviour */
  }
}

function recall(): Remembered | null {
  try {
    const raw = localStorage.getItem(REMEMBERED);
    if (!raw) return null;
    const value = JSON.parse(raw) as Remembered;
    return typeof value?.accounts === 'boolean' ? value : null;
  } catch {
    return null;
  }
}

/** Forget the remembered standing - on sign-out, so a shared device does not open offline as someone else. */
export function forgetSession(): void {
  try {
    localStorage.removeItem(REMEMBERED);
  } catch {
    /* nothing to forget */
  }
}

/** True while the app is running from the cache with no server behind it. */
export function isOfflineMode(): boolean {
  return gate.state === 'offline' && gate.offlineWith !== null;
}

export const gate: {
  state: GateState; error: string; busy: boolean; accounts: boolean;
  registration: 'invite' | 'closed'; inviteCode: string;
  account: api.SessionState['account'] | null;
  /** The remembered standing the app is running on while offline, or null. */
  offlineWith: { accounts: boolean; account: api.SessionState['account'] | null } | null;
} = $state({
  state: 'checking',
  error: '',
  busy: false,
  accounts: false,
  registration: 'invite',
  inviteCode: '',
  account: null,
  offlineWith: null
});

/** Strip `?invite=` from the address bar without adding a history entry. */
function scrubInviteFromUrl(): void {
  const url = new URL(location.href);
  if (!url.searchParams.has('invite')) return;
  url.searchParams.delete('invite');
  history.replaceState(history.state, '', url.pathname + (url.search || '') + url.hash);
}

api.whenSignedOut(forgetSession);

let checkGeneration = 0;
let checkController: AbortController | null = null;

/** Check the mode before considering a legacy invite redemption. */
export async function openGate(): Promise<void> {
  const generation = ++checkGeneration;
  checkController?.abort();
  const controller = new AbortController();
  checkController = controller;
  gate.state = 'checking';
  const inviteInUrl = new URL(location.href).searchParams.get('invite');
  gate.error = '';
  try {
    let state = await api.session(controller.signal);
    if (generation !== checkGeneration) return;
    gate.accounts = state.accounts === true;
    gate.account = state.account ?? null;
    gate.registration = state.registration ?? 'invite';
    if (gate.accounts) {
      // An invitation is only a registration field, never account authentication.
      gate.inviteCode = inviteInUrl ?? gate.inviteCode;
      scrubInviteFromUrl();
    } else if (inviteInUrl) {
      try {
        const redeemed = await api.redeem(inviteInUrl);
        if (generation !== checkGeneration) return;
        if (redeemed.authenticated) state = redeemed;
      } catch (err) {
        if (err instanceof api.ApiError && err.status === 0) throw err;
        // The existing session can still be valid after a rejected invite.
      }
      if (generation !== checkGeneration) return;
      scrubInviteFromUrl();
    }
    gate.state = state.authenticated || (!gate.accounts && state.open) ? 'open' : 'locked';
    gate.offlineWith = null;
    if (gate.state === 'open') {
      remember(state);
      void loadProviderFeatures();
    }
  } catch {
    if (generation !== checkGeneration) return;
    // Without a successful mode check, do not offer legacy redemption.
    gate.state = 'offline';
    gate.error = 'Cannot reach the server.';
    /* But a browser that was let in before is still that browser. Run on
       what we last knew, so the downloads are reachable. See the note on
       GateState. */
    const known = recall();
    if (known) {
      gate.accounts = known.accounts;
      gate.account = known.account;
      gate.offlineWith = { accounts: known.accounts, account: known.account };
    }
  } finally {
    if (generation === checkGeneration) checkController = null;
  }
}

/** A fresh document prevents a previous user's in-memory social state surviving. */
export async function submitAccount(
  mode: 'login' | 'register', input: api.AccountRegistration
): Promise<boolean> {
  if (gate.busy || !gate.accounts) return false;
  gate.busy = true;
  gate.error = '';
  try {
    const state = mode === 'register'
      ? await api.registerAccount(input)
      : await api.loginAccount({ email: input.email, password: input.password, deviceLabel: input.deviceLabel });
    if (!state.authenticated) {
      gate.error = 'Sign-in was not completed. Try again.';
      return false;
    }
    if(['/signin','/welcome'].includes(location.pathname))location.assign('/');
    else location.reload();
    return true;
  } catch (err) {
    gate.error = api.accountError(err);
    return false;
  } finally {
    gate.busy = false;
  }
}

/** The code field on the lock screen. */
export async function submitCode(code: string): Promise<boolean> {
  if (gate.accounts || gate.busy || gate.state !== 'locked') return false;
  const trimmed = code.trim();
  if (!trimmed) return false;
  gate.busy = true;
  gate.error = '';
  try {
    const state = await api.redeem(trimmed);
    if (state.authenticated) {
      gate.state = 'open';
      void loadProviderFeatures();
      return true;
    }
    gate.error = 'That code was not accepted.';
    return false;
  } catch (err) {
    gate.error =
      err instanceof api.ApiError && err.status === 401
        ? 'That code was not accepted.'
        : 'Could not reach the server. Try again.';
    return false;
  } finally {
    gate.busy = false;
  }
}
